/**
 * FeedScreen — THE product. Prioritized anomaly feed (TZ §4.9): the move from *watching*
 * to *managing*. New cards arriving over the channel animate in at the top.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AnomalyCard } from "@/types/contract";
import { api, UnauthorizedError } from "@/api/client";
import { ScreenHeader } from "@/components/ScreenHeader";
import { AnomalyCardItem } from "@/components/anomaly/AnomalyCardItem";
import {
  FeedFilters,
  type FeedFilterState,
} from "@/components/anomaly/FeedFilters";
import { FeedEmpty, FeedError, FeedSkeleton } from "@/components/anomaly/FeedStates";
import { ClipReviewModal } from "@/components/clip/ClipReviewModal";
import { useFeedChannel, type ConnectionState } from "@/hooks/useFeedChannel";
import { sortAnomalies } from "@/lib/anomaly";

interface Props {
  restaurants: { id: string; name: string }[];
  restaurantId: string;
  onRestaurantChange: (id: string) => void;
  onConnectionChange: (s: ConnectionState) => void;
}

type Phase =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

export function FeedScreen({
  restaurants,
  restaurantId,
  onRestaurantChange,
  onConnectionChange,
}: Props) {
  const [cards, setCards] = useState<AnomalyCard[]>([]);
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [clipCard, setClipCard] = useState<AnomalyCard | null>(null);

  const [filters, setFilters] = useState<FeedFilterState>({
    status: "all",
    category: "all",
    restaurantId,
  });

  // Keep filter.restaurantId in sync with the app-level selection.
  useEffect(() => {
    setFilters((f) => ({ ...f, restaurantId }));
  }, [restaurantId]);

  const load = useCallback(async () => {
    setPhase({ kind: "loading" });
    try {
      const list = await api.getAnomalies({ restaurant_id: restaurantId });
      setCards(list);
      setPhase({ kind: "ready" });
    } catch (e) {
      const message =
        e instanceof UnauthorizedError
          ? "Сессия истекла — войдите в Orees Cloud снова."
          : e instanceof Error
            ? e.message
            : "Неизвестная ошибка";
      setPhase({ kind: "error", message });
    }
  }, [restaurantId]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Real-time channel ─────────────────────────────────────────────────────
  const markNew = useCallback((id: string) => {
    setNewIds((prev) => new Set(prev).add(id));
    // Clear the "new" highlight after the animation window.
    window.setTimeout(() => {
      setNewIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 6000);
  }, []);

  const handleNew = useCallback(
    (card: AnomalyCard) => {
      setCards((prev) => {
        if (prev.some((c) => c.id === card.id)) {
          return prev.map((c) => (c.id === card.id ? card : c));
        }
        return [card, ...prev];
      });
      markNew(card.id);
    },
    [markNew],
  );

  const handleUpdate = useCallback((card: AnomalyCard) => {
    setCards((prev) =>
      prev.some((c) => c.id === card.id)
        ? prev.map((c) => (c.id === card.id ? card : c))
        : [card, ...prev],
    );
  }, []);

  const handlePollSnapshot = useCallback((list: AnomalyCard[]) => {
    setCards((prev) => {
      // Detect genuinely new ids for the animation.
      const prevIds = new Set(prev.map((c) => c.id));
      const fresh = list.filter((c) => !prevIds.has(c.id));
      if (fresh.length > 0) {
        setNewIds((s) => {
          const next = new Set(s);
          fresh.forEach((c) => next.add(c.id));
          return next;
        });
      }
      return list;
    });
  }, []);

  const connection = useFeedChannel({
    restaurantId,
    onNew: handleNew,
    onUpdate: handleUpdate,
    onPollSnapshot: handlePollSnapshot,
  });

  const lastConn = useRef<ConnectionState | null>(null);
  useEffect(() => {
    if (lastConn.current !== connection) {
      lastConn.current = connection;
      onConnectionChange(connection);
    }
  }, [connection, onConnectionChange]);

  // ── Actions (optimistic) ──────────────────────────────────────────────────
  const setStatus = useCallback(
    async (card: AnomalyCard, status: AnomalyCard["status"]) => {
      setBusyId(card.id);
      const prev = card.status;
      setCards((cs) =>
        cs.map((c) => (c.id === card.id ? { ...c, status } : c)),
      );
      try {
        await api.setAnomalyStatus(card.id, status);
      } catch {
        // Roll back on failure.
        setCards((cs) =>
          cs.map((c) => (c.id === card.id ? { ...c, status: prev } : c)),
        );
      } finally {
        setBusyId(null);
      }
    },
    [],
  );

  // ── Derived view ──────────────────────────────────────────────────────────
  const visible = useMemo(() => {
    const filtered = cards.filter((c) => {
      if (filters.status !== "all" && c.status !== filters.status) return false;
      if (filters.category !== "all" && c.category !== filters.category)
        return false;
      return true;
    });
    return sortAnomalies(filtered);
  }, [cards, filters]);

  const openCount = cards.filter((c) => c.status === "open").length;

  return (
    <>
      <ScreenHeader
        eyebrow="Главный экран"
        title="Лента аномалий"
        subtitle={
          openCount > 0
            ? `${openCount} открытых · отсортировано по приоритету`
            : "Открытых аномалий нет"
        }
      />

      <FeedFilters
        value={filters}
        restaurants={restaurants}
        onChange={(next) => {
          setFilters(next);
          if (next.restaurantId !== restaurantId) {
            onRestaurantChange(next.restaurantId);
          }
        }}
      />

      {phase.kind === "loading" && <FeedSkeleton />}
      {phase.kind === "error" && (
        <FeedError message={phase.message} onRetry={() => void load()} />
      )}
      {phase.kind === "ready" && visible.length === 0 && <FeedEmpty />}
      {phase.kind === "ready" && visible.length > 0 && (
        <div className="flex flex-col gap-3" aria-live="polite">
          {visible.map((card) => (
            <AnomalyCardItem
              key={card.id}
              card={card}
              isNew={newIds.has(card.id)}
              busy={busyId === card.id}
              onOpenClip={setClipCard}
              onAcknowledge={(c) => void setStatus(c, "acknowledged")}
              onResolve={(c) => void setStatus(c, "resolved")}
            />
          ))}
        </div>
      )}

      {clipCard && (
        <ClipReviewModal card={clipCard} onClose={() => setClipCard(null)} />
      )}
    </>
  );
}
